import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Eye, EyeOff, Trash2, ExternalLink, Newspaper, CalendarClock } from "lucide-react";
import { api } from "../lib/api";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastJob, setLastJob] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [p, j] = await Promise.all([api.get("/admin/blog"), api.get("/admin/blog/jobs")]);
      setPosts(p.data);
      const job = j.data[0] || null;
      setLastJob(job);
      setGenerating(job?.status === "running");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!generating) return undefined;
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [generating, load]);

  const generate = async () => {
    setGenerating(true);
    await api.post("/admin/blog/generate");
    load();
  };

  const toggle = async (post) => {
    const status = post.status === "published" ? "hidden" : "published";
    await api.patch(`/admin/blog/${post.id}`, { status });
    setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, status } : p)));
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή άρθρου;")) return;
    await api.delete(`/admin/blog/${id}`);
    setPosts((ps) => ps.filter((p) => p.id !== id));
  };

  return (
    <div data-testid="admin-blog-tab">
      <div className="pcb-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <CalendarClock className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="text-white font-semibold">Αυτόματη δημοσίευση με AI</div>
            <div className="text-sm text-emerald-100/70 mt-0.5">
              Νέο άρθρο κάθε Δευτέρα, Τετάρτη & Παρασκευή στις 07:00. Σύνολο άρθρων: {posts.length}
            </div>
            {lastJob?.status === "failed" && (
              <div className="text-xs text-red-300 mt-1" data-testid="blog-job-error">
                Η τελευταία προσπάθεια απέτυχε: {lastJob.error}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="blog-generate-btn"
          className="btn-primary rounded-md px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Γράφεται άρθρο… (~1 λεπτό)" : "Γράψε άρθρο τώρα"}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-emerald-300">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="pcb-card p-12 text-center text-emerald-100/70" data-testid="blog-admin-empty">
          <Newspaper className="w-8 h-8 mx-auto text-emerald-400 mb-3" />
          Δεν υπάρχουν άρθρα ακόμη. Πάτα «Γράψε άρθρο τώρα».
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} className="pcb-card p-4 flex flex-col md:flex-row gap-4" data-testid={`admin-blog-post-${post.id}`}>
              <img src={post.image} alt="" className="w-full md:w-40 h-28 object-cover rounded-md shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300">{post.category_label}</span>
                  <span
                    className={`px-2 py-0.5 rounded border ${
                      post.status === "published"
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : "bg-slate-500/15 border-slate-500/40 text-slate-300"
                    }`}
                  >
                    {post.status === "published" ? "Δημοσιευμένο" : "Κρυφό"}
                  </span>
                  <span className="text-emerald-100/50">{formatDate(post.created_at)}</span>
                </div>
                <div className="mt-1.5 text-white font-semibold">{post.title}</div>
                <div className="mt-1 text-sm text-emerald-100/70 line-clamp-2">{post.excerpt}</div>
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                <a
                  href={`/blog/${post.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md px-3 py-2 text-sm btn-outline inline-flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Άνοιγμα
                </a>
                <button
                  onClick={() => toggle(post)}
                  data-testid={`blog-toggle-${post.id}`}
                  className="rounded-md px-3 py-2 text-sm border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 transition-colors inline-flex items-center justify-center gap-2"
                >
                  {post.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {post.status === "published" ? "Απόκρυψη" : "Δημοσίευση"}
                </button>
                <button
                  onClick={() => remove(post.id)}
                  data-testid={`blog-delete-${post.id}`}
                  className="rounded-md px-3 py-2 text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Διαγραφή
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
